import type { PhoneInputController } from 'application/ui/fields/phone'

/**
 * Класс для валидации данных бронирования и отображения ошибок
 */
export class BookingValidator {
  private modal: HTMLElement

  constructor(modal: HTMLElement) {
    this.modal = modal
  }

  /**
   * Валидация email
   * @param {string} email - email для проверки
   * @return {boolean} true если email валиден
   */
  isValidEmail(email: string): boolean {
    // Держим в согласии с EmailValidator::REGEXP на сервере. Прежняя проверка
    // «что угодно @ что угодно . что угодно» пропускала опечатки, которые потом
    // отбивал партнёр («Неправильный формат email»), и бронь терялась: клиент
    // видел ошибку уже после ввода кода из SMS, площадка о нём не узнавала.
    const emailRegex =
      /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+(?:[A-Za-z]{2,63}|xn--[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*)$/
    // Как EmailValidator.normalize: невидимые символы убираем, пробелы по
    // краям (включая неразрывный) срезаем.
    const value = email.replace(/[\u00AD\u200B-\u200D\u2060\uFEFF]/g, '').trim()
    if (!emailRegex.test(value)) return false

    // Пределы RFC 5321: длиннее почтовые серверы адрес не принимают.
    const atSign = value.lastIndexOf('@')
    return (
      value.length <= 254 &&
      atSign <= 64 &&
      value
        .slice(atSign + 1)
        .split('.')
        .every((label) => label.length <= 63)
    )
  }

  /**
   * Показать общую ошибку в модалке
   * @param {string} message - текст ошибки
   */
  showError(message: string): void {
    this.clearError()

    // Контейнер ошибки есть в каждом шаге над кнопками: сообщение появляется
    // рядом с кнопкой, а не за нижней кромкой окна.
    const errorContainer =
      this.modal.querySelector<HTMLElement>(
        '[data-booking-step].is-active [data-booking-error]'
      ) || this.modal.querySelector<HTMLElement>('[data-booking-error]')

    if (!errorContainer) return

    const p = document.createElement('p')
    p.textContent = message
    errorContainer.appendChild(p)
  }

  /**
   * Очистить общие ошибки
   */
  clearError(): void {
    this.modal
      .querySelectorAll<HTMLElement>('[data-booking-error]')
      .forEach((container) => {
        container.innerHTML = ''
      })
  }

  /**
   * Показать ошибку для SMS кода
   * @param {string} message - текст ошибки
   */
  showCodeError(message: string): void {
    const errorEl = this.modal.querySelector(
      '[data-sms-error], [data-code-error]'
    ) as HTMLElement
    const codeInput = this.getCodeInput()

    codeInput?.classList.add('has-error')

    if (errorEl) {
      errorEl.textContent = message
      errorEl.classList.remove('is-hidden')
    }
  }

  /**
   * Скрыть ошибку SMS кода
   */
  hideCodeError(): void {
    const errorEl = this.modal.querySelector(
      '[data-sms-error], [data-code-error]'
    ) as HTMLElement
    const codeInput = this.getCodeInput()

    codeInput?.classList.remove('has-error')

    if (errorEl) {
      errorEl.textContent = ''
      errorEl.classList.add('is-hidden')
    }
  }

  private getCodeInput(): HTMLInputElement | null {
    return this.modal.querySelector(
      '[data-code-input]'
    ) as HTMLInputElement | null
  }

  /**
   * Показать ошибку промокода
   * @param {string} message - текст ошибки
   */
  showPromoCodeError(message: string): void {
    const wrapper = this.modal.querySelector('[data-promo-code]')
    const errorElement = this.modal.querySelector(
      '[data-promo-error]'
    ) as HTMLElement

    wrapper?.classList.add('error')
    wrapper?.classList.remove('accepted')

    if (errorElement) {
      errorElement.textContent = message
      errorElement.style.display = 'block'
    }
  }

  /**
   * Показать успешное применение промокода
   */
  showPromoCodeSuccess(): void {
    const wrapper = this.modal.querySelector('[data-promo-code]')
    wrapper?.classList.remove('error')
    wrapper?.classList.add('accepted')
  }

  /**
   * Валидация данных для отправки SMS
   * @param {string} name - имя пользователя
   * @param {string} phone - номер телефона
   * @param {string} email - email пользователя
   * @param {PhoneInputController | null} phoneController - контроллер телефона
   * @return {string | null} текст ошибки или null
   */
  validateSmsData(
    name: string,
    phone: string,
    email: string,
    phoneController: PhoneInputController | null
  ): string | null {
    if (!name || !phone || !email) {
      return 'Необходимо заполнить имя, телефон и email'
    }

    if (!this.isValidEmail(email)) {
      return 'Укажите корректный email'
    }

    if (phoneController && !phoneController.isValid()) {
      return 'Укажите корректный номер телефона'
    }

    return null
  }
}
